import { z } from "zod";

const pos = z.number().positive();
const nonneg = z.number().nonnegative();
const angleDeg = z.number().min(0).max(89);

export const StepSchema = z.object({ height: pos, topThickness: pos, bottomThickness: pos });

export const ParametricGeometrySchema = z.object({
  mode: z.literal("parametric"),
  frontBatter: nonneg.max(1),                       // yatay/düşey
  steps: z.array(StepSchema).min(1).max(4),         // üstten alta
  foundation: z.object({ height: pos, toeExtension: nonneg, heelExtension: nonneg }).optional(),
  embedmentDepth: nonneg,                           // Df
  /**
   * Eğik taban (KGM Karayolu Tasarım El Kitabı Şekil 4.14–4.17, harçlı taş duvar): tan α, topuk alçak. 0/yok = yatay taban.
   * Kabul: burun (0,0) sabit, topuk (B, −B·tanα) — gövde, Df (burundan ön zemine), su kotları aynı kot sisteminde kalır;
   * itki yüksekliği topuğun en alt noktasından ölçülür (B·tanα kadar artar). Temel bloğu varsa alt yüzü aynı eğimle iner,
   * üst yüzü (gövde tabanı) yatay kalır.
   */
  baseSlope: nonneg.max(0.3).optional(),
});

export const PolygonGeometrySchema = z.object({
  mode: z.literal("polygon"),
  points: z.array(z.object({ x: z.number(), y: z.number() })).min(3),
  embedmentDepth: nonneg,
  bodyBaseY: nonneg.optional(),                     // temel bloğu üst kotu (metraj ve temel üstü derz)
  jointLevels: z.array(pos).optional(),             // elle derz kotları; boş/yok → yatay kademe kenarları otomatik
});

/**
 * Betonarme konsol (T/L) duvar: gövde + taban plağı (burun/topuk), isteğe bağlı diş ve payandalar.
 * Stabilite çokgene çevrilerek mevcut motorla (topuktan geçen düşey sanal düzlem); diş çokgene girmez (pasif derinlik + ağırlık).
 */
export const RcCantileverGeometrySchema = z.object({
  mode: z.literal("rc-cantilever"),
  stem: z.object({
    height: pos, topThickness: pos, bottomThickness: pos,
    /** Kalınlaşma hangi yüzde: front = arka yüz düşey, ön yüz eğik (yaygın) · back = ön yüz düşey */
    taperSide: z.enum(["front", "back"]).default("front"),
  }).refine((s) => s.topThickness <= s.bottomThickness + 1e-9, { message: "Gövde üst kalınlığı alt kalınlıktan büyük olamaz" }),
  base: z.object({ thickness: pos, toeLength: nonneg, heelLength: nonneg }),
  /** Kayma dişi (taban altında): derinlik, genişlik, burun ucundan uzaklık */
  key: z.object({ depth: pos, width: pos, offsetFromToe: nonneg }).optional(),
  /** Payandalar (arka yüzde, topuk üstünde): aralık s (eksenden eksene), kalınlık, üstte genişlik (0 = üçgen) */
  counterforts: z.object({ spacing: pos, thickness: pos, topWidth: nonneg.default(0) }).optional(),
  embedmentDepth: nonneg,
});

export const GeometrySchema = z.discriminatedUnion("mode", [ParametricGeometrySchema, PolygonGeometrySchema, RcCantileverGeometrySchema]);

export const SoilLayerSchema = z.object({
  name: z.string().optional(),
  thickness: pos,
  gamma: pos,
  gammaSat: pos.optional(),
  phi: angleDeg,
  c: nonneg,
});

export const BackfillSchema = z.object({
  layers: z.array(SoilLayerSchema).min(1),
  slopeAngle: angleDeg,                              // β
  /** δ: 2φ/3 (TBDY üst sınırı) · "beta": δ = β — düşey sanal düzlemde Rankine (konsol/T duvar pratiği, EC7 örnekleri; 2φ/3 sınırı ve γφ uygulanmaz) · sayı (≤ 2φ/3) */
  wallFrictionAngle: z.union([z.literal("two-thirds-phi"), z.literal("beta"), angleDeg]),
  permeability: z.enum(["permeable", "impermeable"]),
});

export const FoundationSoilSchema = z.object({
  gamma: pos,
  phi: angleDeg,
  c: nonneg,
  /** Taban sürtünmesi: 2φ/3 · φ · elle δ · concrete = grobeton/beton temel üstünde tanδ = 0,6 (TBDY Tablo 16.3; Cem 2026-09-29: seçenek, varsayılan 2φ/3) */
  baseFriction: z.object({ mode: z.enum(["two-thirds-phi", "phi", "custom", "concrete"]), value: angleDeg.optional() }),
  adhesion: nonneg,
  bearing: z.object({
    mode: z.enum(["given", "computed"]),
    qk: pos.optional(),
    sigmaAllow: pos.optional(),
    ratio: pos,
    /**
     * Depremli durumda taşıma gücü yöntemi: tbdy-16.8a (hüküm) · ec8-annex-f: EN 1998-5 Ek F yüzeyi ek olarak hesaplanır
     * (bilgi amaçlı; hüküm TBDY'de kalır — spec §4.2)
     */
    seismicMethod: z.enum(["tbdy-16.8a", "ec8-annex-f"]).default("tbdy-16.8a"),
    /** EN 1998-5 Tablo F.2 γRd kategorisi; auto: drenajlı → kum (φ ≥ 33° sıkı, aksi gevşek; ön su varsa doygun), drenajsız → hassas olmayan kil */
    ec8Category: z.enum(["auto", "dense-sand", "loose-dry-sand", "loose-saturated-sand", "non-sensitive-clay", "sensitive-clay"]).default("auto"),
  }),
  passive: z.object({ enabled: z.boolean(), neglectedDepth: nonneg }),
  drained: z.boolean(),
});

export const WaterSchema = z.object({
  backLevel: pos.nullable(),
  frontLevel: pos.nullable(),
  gammaWater: pos,
});

export const LineLoadSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  kind: z.enum(["strip", "line"]),
  magnitude: pos,
  distance: nonneg,
  width: pos.optional(),
});

export const LoadsSchema = z.object({
  surcharge: nonneg,
  lineLoads: z.array(LineLoadSchema),
  selfWeightFactorSeismic: z.union([z.literal(1.0), z.literal(0.9)]),
});

export const SiteClassSchema = z.enum(["ZA", "ZB", "ZC", "ZD", "ZE"]);

export const SeismicSchema = z.object({
  enabled: z.boolean(),
  /**
   * map: SS/S1/zemin → SDS (TBDY) · direct: SDS doğrudan (TBDY) · coefficients: kh, kv doğrudan ·
   * ec8: ag·S, γI, r (EN 1998-5 §7.3.2) · aashto: PGA·FPGA, kh = 0,5·kmax yer değiştirmeye izin varsa (AASHTO LRFD 11.6.5)
   */
  inputMode: z.enum(["map", "direct", "coefficients", "ec8", "aashto"]),
  level: z.enum(["DD-1", "DD-2", "DD-3", "DD-4"]),
  SS: pos.optional(),
  S1: pos.optional(),
  siteClass: SiteClassSchema.optional(),
  SDS: pos.optional(),
  /** coefficients modunda: yatay ve düşey statik-eşdeğer deprem katsayıları (kv her iki işaretle de denenir) */
  kh: z.number().min(0).max(1).optional(),
  kv: z.number().min(0).max(1).optional(),
  /** ec8: referans yer ivmesi ag/g (agR·γI/g), zemin katsayısı S, avg/ag oranı (kv: > 0,6 → 0,5·kh, aksi 0,33·kh) */
  ec8: z.object({ agOverG: pos.max(2), S: pos.max(3), gammaI: pos.max(2).default(1.0), avgOverAg: nonneg.max(2).default(0.9) }).optional(),
  /** aashto: PGA (g), FPGA zemin katsayısı, yer değiştirmeye izin (kh = 0,5·kmax) */
  aashto: z.object({ pga: pos.max(2), fpga: pos.max(3).default(1.0), displacementAllowed: z.boolean().default(true) }).optional(),
  r: z.union([z.literal(2.0), z.literal(1.5), z.literal(1.0)]),
  saturatedSoilLimitsR: z.boolean(),
  dynamicPointOfApplication: z.enum(["H/2", "0.6H"]),
  includeWallInertia: z.boolean(),
  /**
   * Topuk/kama üstü dolgu ve sürşarj ataleti kh·W_dolgu (duvar ataleti açıkken). TBDY 16.12 atalet tanımlamaz; istCAD 2021
   * ("geri dolgu deprem kuvveti" varsayılan kapalı), iCad yalnız gövde alır; EN 1998-5 / AASHTO dolguyu da katar (Cem 2026-10-05:
   * yeni duvarda kapalı). Eski projelerde alan yok → açık (önceki sonuçlar değişmez).
   */
  includeBackfillInertia: z.boolean().default(true),
  /**
   * TBDY 2018 Denk. 16.23 harfiyen: (1 ± kv) toplam aktif itkiye (statik kısım dahil) uygulanır (istCAD, iCad, ProtaDetails bu
   * biçimde). Eski projelerde alan yok → false: yalnız dinamik artım (1 ± kv) ile çarpılır (önceki sonuçlar değişmez). Yeni duvarda açık.
   */
  kvOnTotalPressure: z.boolean().default(false),
});

export const ConcreteClassSchema = z.enum(["C20", "C25", "C30", "C35", "C40", "C45", "C50"]);
export const SteelGradeSchema = z.enum(["B420C", "B500C"]);
export const RcDesignCodeSchema = z.enum(["auto", "ts500", "ec2", "aci318"]);
export const BAR_DIAMETERS = [12, 14, 16, 18, 20, 22, 25] as const;
export const BAR_SPACINGS = [0.1, 0.125, 0.15, 0.2, 0.25] as const;
/** Elle seçilen donatı: çap (mm) ve aralık (m) */
export const BarSpecSchema = z.object({ dia: z.number().int().min(8).max(40), spacing: pos.max(0.5) });
/**
 * Betonarme kesit tasarımı ayarları (yalnız rc-cantilever modunda kullanılır). code "auto": TBDY → TS 500, EC7 → EN 1992-1-1, ABD → ACI 318-19.
 * `bars`: verilen konumlar elle seçilmiş sayılır ve denetlenir; verilmeyenler otomatik (en ekonomik) seçilir.
 */
export const RcSettingsSchema = z.object({
  concrete: ConcreteClassSchema.default("C25"),
  steel: SteelGradeSchema.default("B420C"),
  /** Pas payı (m); zeminle temas eden yüzeyler için 0,05 (TS 500 §9.6) */
  cover: pos.max(0.15).default(0.05),
  code: RcDesignCodeSchema.default("auto"),
  /** Gövde kesit kotları aralığı (m) */
  stemLevelStep: pos.default(0.5),
  /** Topuk tasarımında taban basıncı (lehte) düşülsün mü (ACI ders notu pratiği: hayır — daha tutucu) */
  heelUplift: z.boolean().default(true),
  bars: z.object({
    stemMain: BarSpecSchema.optional(), stemFront: BarSpecSchema.optional(), stemHorizontal: BarSpecSchema.optional(),
    toeMain: BarSpecSchema.optional(), heelMain: BarSpecSchema.optional(), baseDistribution: BarSpecSchema.optional(),
  }).default({}),
});

/** Metraj ayarları (duvar düzeyi). Kazı şevi yatay/düşey (0 = dik); çalışma payı temel kenarından; grobeton kalınlığı 0 → yok. */
export const QuantitiesSettingsSchema = z.object({
  workingSpace: nonneg.max(2).default(0.5),
  excavationSlope: nonneg.max(2).default(0.5),
  blindingThickness: nonneg.max(0.5).default(0.1),
  blindingOverhang: nonneg.max(1).default(0.1),
  /** Taş duvarda harç hacim oranı (moloz taş 0,30–0,35; kaba yonu 0,25) */
  mortarRatio: nonneg.max(0.6).default(0.3),
  /** Ocak taşı gevşek hacim kabarması (duvar içindeki sıkışık taş hacmine göre) */
  stoneBulking: pos.max(2).default(1.25),
  /** Taş / beton / harç fire payı (ÇŞB pozlarında fire birim fiyata dâhildir → 0) */
  wasteFactor: nonneg.max(0.5).default(0),
  /** Donatı bindirme + fire payı */
  steelExtra: nonneg.max(0.5).default(0.1),
  drainage: z.object({
    enabled: z.boolean().default(true),
    pipeDiameter: pos.max(500).default(100),          // mm
    filterThickness: nonneg.max(2).default(0.3),      // m
    barbacaneSpacing: pos.max(10).default(2),         // m
    barbacaneDiameter: pos.max(300).default(100),     // mm
  }).default({}),
});

export const QUANTITY_KEYS = ["excavation", "backfill", "blinding", "masonry", "stone", "mortar", "concrete", "formwork", "rebarSmall", "rebarLarge", "drainPipe", "filter", "barbacane"] as const;
export const QuantityKeySchema = z.enum(QUANTITY_KEYS);
/** Poz satırı: ÇŞB poz no, tanım, birim (boş → metraj birimi), birim fiyat (KDV hariç) — hepsi kullanıcı tarafından düzenlenebilir */
export const PriceItemSchema = z.object({ key: QuantityKeySchema, code: z.string().default(""), label: z.string().default(""), unit: z.string().default(""), unitPrice: nonneg.default(0) });
/** Keşif notu varsayılanı: raporda, XLSX/CSV ve Word'de basılır; kullanıcı düzenleyebilir. */
export const DEFAULT_PRICING_NOTE = "Birim fiyatlar ÇŞB Yüksek Fen Kurulu 2026 Temmuz ayı güncel inşaat birim fiyat listesinden alınmıştır (%25 yüklenici kârı ve genel giderler dâhil, KDV hariç). Liste TÜİK endeksleriyle her ay güncellenir; iş programınıza uyan ayın resmî listesiyle kontrol edip poz tablosundan güncelleyin.";
/** Proje düzeyinde birim fiyat tablosu (keşif özeti); sağlayıcı paneli varsayılanı verir. */
export const PricingSchema = z.object({ currency: z.string().default("₺"), year: z.string().default(""), note: z.string().default(DEFAULT_PRICING_NOTE), items: z.array(PriceItemSchema).default([]) });

export const StonePresetSchema = z.object({
  wallType: z.enum(["kuru", "moloz", "kaba-yonu", "yonu", "kesme"]),
  stone: z.string(),                                 // materials.ts STONE_KINDS id
  mortar: z.enum(["A", "B", "CD", "E"]),
});
export const MaterialSchema = z.object({
  gamma: pos,
  allowableCompression: pos,
  jointFriction: pos,
  jointCohesion: nonneg,
  description: z.string().optional(),
  /** Seçilen taş duvar ön ayarı (TS 498 + TS 2510); sayılar elle değiştirilince kaldırılır ("Özel") */
  preset: StonePresetSchema.optional(),
});

const pair = z.object({ static: pos, seismic: pos });
export const DesignCodeSchema = z.enum(["tbdy2018", "ec7", "us"]);
export type DesignCode = z.infer<typeof DesignCodeSchema>;

/** EN 1997-1 tasarım yaklaşımı ve ulusal ek (kısmi katsayı setleri codes/ec.ts tablosunda). */
export const EcOptionsSchema = z.object({
  approach: z.enum(["DA1", "DA2", "DA3"]).default("DA1"),
  nationalAnnex: z.enum(["EN", "UK", "DE"]).default("EN"),
  /** Depremli kombinasyonda sürşarj için ψ2 (EN 1990 Tablo A1.1; yol/park 0,3, depo 0,8) */
  psi2: nonneg.max(1).default(0.3),
});

export const CheckSettingsSchema = z.object({
  classic: z.object({
    overturning: pair, sliding: pair, bearing: pair, eccentricity: pair, jointShear: pair, jointEccentricity: pair,
  }),
  /** γRdev: statik (TBDY ≥ 1,3; pratikte 1,5–2,5) ve depremli (≥ 1,3) ayrı seçilir; eski projelerde yalnız gammaRdev vardı → deprem 1,3 */
  tbdy: z.object({
    gammaRdev: z.number().min(1.3), gammaRdevSeismic: z.number().min(1.3).default(1.3),
    /**
     * Drenajlı kaymada adezyon c_a·A_c de dirence eklensin mi (Cem 2026-10-01: istCAD gibi seçenek). TBDY 16.8.4 drenajlı
     * durumda yalnız Ptv·tanδ verir (Denk. 16.10; c_u ancak drenajsızda, Denk. 16.11) → yönetmelik dışı, varsayılan kapalı.
     */
    adhesionInSliding: z.boolean().default(false),
  }),
  includeActiveVerticalComponent: z.boolean(),
  /** Kama üstü sürşarjın düşey ağırlığı devrilme/kaymada lehte sayılsın mı (v1.0: evet; EC7/ders notu pratiği: hayır) */
  surchargeAsWeight: z.boolean().default(true),
  extraJointLevels: z.array(pos),
  /** Yönetmelik profili: doğrulama biçimi, deprem katsayısı türetimi, hedefler ve rapor atıfları */
  designCode: DesignCodeSchema.default("tbdy2018"),
  /**
   * virtual-plane: topuktan geçen sanal düzlem (v1.0; kama ağırlığı düşey yük) ·
   * back-face: itki gerçek arka yüze kademe kademe (her kademe kendi ψᵢ ile Coulomb / M-O), kademe çıkıntısı üstündeki dolgu düşey yük
   */
  pressureMethod: z.enum(["virtual-plane", "back-face"]).default("virtual-plane"),
  ec: EcOptionsSchema.default({}),
  /**
   * TBDY profilinde klasik GK (devrilme/kayma hedefleri, statik e ≤ B/6) da geçme şartı mı? false → yalnız TBDY dayanım katsayıları
   * (16.8, 16.21) hüküm verir; klasik devrilme/kayma GK bilgi olarak raporlanır, taban dış merkezliliği en çok B/3 (Cem 2026-10-05).
   * Taşıma gücünde q ≤ σem kontrolü sürer. Eski projelerde alan yok → true (önceki sonuçlar değişmez).
   */
  classicGoverns: z.boolean().default(true),
});

/**
 * Toptan göçme (şev stabilitesi) ayarları — dilim yöntemi, dairesel kayma yüzeyi taraması.
 * Ana `analyzeWall` bunu ÇAĞIRMAZ; `analyzeGlobalStability` ayrı çalışır (web'de Web Worker'da).
 * Eski JSON'larda alan yoksa varsayılan (kapalı) uygulanır.
 */
export const GlobalStabilitySchema = z.object({
  enabled: z.boolean().default(false),
  method: z.enum(["bishop", "fellenius"]).default("bishop"),
  /** Merkez ızgarası (m, kesit koordinatları). auto: geometriden türetilir (aralık ve adım yok sayılır) */
  searchGrid: z.object({
    auto: z.boolean().default(true),
    xRange: z.tuple([z.number(), z.number()]).default([-2, 8]),
    yRange: z.tuple([z.number(), z.number()]).default([4, 14]),
    step: pos.default(0.5),
  }).default({}),
  /** Yarıçap artımı (m); verilmezse H/8 */
  radiusStep: pos.optional(),
  /** Dairelerin taban kotunun en az bu kadar altına inmesi istenir (m) */
  minDepthBelowBase: nonneg.default(0.5),
  /** Dilim sayısı */
  slices: z.number().int().min(5).max(200).default(30),
  /** pseudo-static: aynı kh ile depremli durum da hesaplanır · none: yalnız statik */
  seismic: z.enum(["pseudo-static", "none"]).default("pseudo-static"),
  /** Hedef GK: TBDY 16.6.2 / 16.12.1.4 pratiği (statik 1,5; depremli 1,1) */
  targets: z.object({ static: pos.default(1.5), seismic: pos.default(1.1) }).default({}),
});

/**
 * DXF paftası ayarları ("Çizim" sekmesi) — yalnız çıktı biçimi, hesaba girmez.
 * Boş (undefined) alanlar Metraj/Malzeme sekmesinden türetilir: barbakan/drenaj çapı ve aralığı, filtre kalınlığı, taş dokusu.
 */
export const DrawingSettingsSchema = z.object({
  unit: z.enum(["cm", "m"]).default("cm"),
  scale: pos.default(50),                                   // 1:scale — yazı/ok boyutu (2,5 mm × ölçek)
  /** Taş dokusu: auto = malzeme tanımından (yonu/kesme → şaşırtmalı sıra, değilse moloz) */
  stone: z.enum(["auto", "moloz", "yonu"]).default("auto"),
  /** Kot işaretlerinin sıfır referansı: ön zemin (D_f), duvar tabanı ya da duvar tepesi */
  levelRef: z.enum(["ground", "base", "top"]).default("ground"),
  /** Referansın mutlak kotu (m); 0 = göreli kotlar (±0,00) */
  levelDatum: z.number().default(0),
  barbacane: z.object({
    show: z.enum(["auto", "on", "off"]).default("auto"),    // auto = Metraj › drenaj açıksa
    diameter: pos.optional(),                                // mm (boş = metrajdaki barbakan çapı)
    hSpacing: pos.default(3),                                // m (yatay aralık, ön görünüş)
    vSpacing: pos.optional(),                                // m (düşey aralık; boş = metrajdaki)
    startHeight: nonneg.default(0.5),                        // m (ön zeminden ilk sıra)
    angleDeg: z.number().min(0).max(30).default(5),          // ° (dışa doğru eğim)
  }).default({}),
  drain: z.object({ show: z.enum(["auto", "on", "off"]).default("auto"), diameter: pos.optional() }).default({}),   // mm
  filterThickness: nonneg.optional(),                        // m (boş = metrajdaki filtre kalınlığı)
  coping: z.object({ enabled: z.boolean().default(true), height: pos.default(0.1), overhang: nonneg.default(0.05) }).default({}),
  anoLength: pos.default(10),                                // m (ano / dilatasyon derzi aralığı)
  /**
   * Çizim paftası (Cem 2026-09-29, AutoCAD layout gibi): kâğıt boyutu ve yönü, görünüm bloklarının elle kaydırması
   * (m, model koordinatı; blok kimlikleri: kesit · gorunus · plan · donati · notlar · antet) ve gizlenen bloklar.
   * auto = çerçeve içeriği sarar (eski davranış).
   */
  sheet: z.object({
    paper: z.enum(["auto", "A4", "A3", "A2", "A1", "A0"]).default("auto"),
    orientation: z.enum(["landscape", "portrait"]).default("landscape"),
    offsets: z.record(z.string(), z.object({ dx: z.number(), dy: z.number() })).default({}),
    hidden: z.array(z.string()).default([]),
    /**
     * Elle düzenlemeler (Cem 2026-09-29: "AutoCAD'e ihtiyacımız kalmasın"): üretilen öğelere anahtar (blok:tür:sıra) ile
     * taşı/ölçekle/döndür (benzerlik dönüşümü s, r°, tx, ty), sil, yazı değiştir; `fp` parmak izi tutmazsa (duvar değişti)
     * düzenleme uygulanmaz. `added`: kopyalar ve elle çizilen çizgi/yazılar (pafta koordinatı, m).
     */
    edits: z.object({
      overrides: z.record(z.string(), z.object({
        fp: z.string(),
        del: z.boolean().optional(),
        tf: z.object({ s: z.number(), r: z.number(), tx: z.number(), ty: z.number(), m: z.boolean().optional() }).optional(),   // m: ayna (önce x eksenine göre)
        text: z.string().max(500).optional(),
        h: z.number().positive().optional(),
        layer: z.string().max(40).optional(),
        color: z.number().int().min(1).max(255).optional(),   // ACI; yoksa katman rengi
      })).default({}),
      added: z.array(z.record(z.string(), z.unknown())).max(5000).default([]),
      /** Patlatılmış (EXPLODE) üretilen bloklar: öğeleri tek tek seçilir */
      exploded: z.array(z.string()).default([]),
      /** Kullanıcı blokları (BLOK YAP): ad → öğe anahtarları; biri seçilince hepsi seçilir */
      blocks: z.record(z.string(), z.array(z.string())).default({}),
    }).default({}),
    /** Kullanıcı katmanları (katman yöneticisinde eklenir; DXF LAYER tablosuna yazılır) — ad · ACI renk */
    layers: z.array(z.object({ name: z.string().min(1).max(40), color: z.number().int().min(1).max(255) })).max(50).default([]),
    /** Ölçü stili (DIMSTYLE): kâğıtta yazı ve ok boyu (mm), ondalık (yoksa cm → 0, m → 2), ok tipi (dolu ok / mimari eğik çizgi "tick") */
    dimStyle: z.object({
      textMm: z.number().min(1).max(10).default(2.5),
      arrowMm: z.number().min(0.5).max(10).default(2.5),
      decimals: z.number().int().min(0).max(3).optional(),
      arrow: z.enum(["closed", "tick"]).default("closed"),
    }).default({}),
  }).default({}),
});

/**
 * Parsel (mülk) sınırı (Cem 2026-10-02): temel bu çizginin dışına taşamaz. `side` sınırın duvarın hangi yanında olduğu —
 * front = ön/burun tarafı (kesitte solda), back = arka/topuk (dolgu) tarafı (sağda); `clearance` gövde yüzünden sınıra yatay
 * uzaklık (0 = gövde yüzü sınırda; betonarmede burun/topuk ≤ clearance). Otomatik boyutlandırma buna uyar, aşılırsa uyarı.
 */
export const PropertyLineSchema = z.object({ side: z.enum(["front", "back"]), clearance: nonneg.default(0) });

export const WallInputSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  length: pos.optional(),
  geometry: GeometrySchema,
  propertyLine: PropertyLineSchema.optional(),
  backfill: BackfillSchema,
  foundationSoil: FoundationSoilSchema,
  water: WaterSchema,
  loads: LoadsSchema,
  seismic: SeismicSchema,
  material: MaterialSchema,
  checks: CheckSettingsSchema,
  globalStability: GlobalStabilitySchema.default({}),
  rc: RcSettingsSchema.default({}),
  quantities: QuantitiesSettingsSchema.default({}),
  drawing: DrawingSettingsSchema.default({}),
});

export const ReportSectionIds = [
  "cover", "projectInfo", "inputs", "drawing", "forceDiagram", "seismicParams", "coefficients", "forces", "steps",
  "stabilityTbdy", "stabilityClassic", "basePressure", "bearingEc8", "joints", "rcDesign", "reinforcement", "globalStability", "quantities", "warnings", "summary", "references",
] as const;
export type ReportSectionId = (typeof ReportSectionIds)[number];

export const ReportSettingsSchema = z.object({
  preset: z.enum(["ozet", "yeterli", "detayli", "ozel"]),
  sections: z.record(z.enum(ReportSectionIds), z.boolean()),
  wallIds: z.union([z.literal("all"), z.array(z.string())]),
});

/** Rapor kapağı özelleştirmesi (proje düzeyi; hepsi isteğe bağlı — boşsa varsayılan başlık/alt başlık ve sağlayıcı logosu). */
export const ReportCoverSchema = z.object({
  title: z.string().max(200).optional(),          // "Statik ve Depremli Stabilite Analizi Raporu" yerine
  subtitle: z.string().max(200).optional(),       // duvar tipine göre üretilen üst başlık yerine
  docNo: z.string().max(60).optional(),           // belge / rapor numarası (kapak + altbilgi)
  reviewer: z.string().max(200).optional(),       // kontrol eden kurum / kişi
  firm: z.object({ name: z.string().max(200).optional(), address: z.string().max(300).optional(), phone: z.string().max(60).optional(), email: z.string().max(120).optional() }).optional(),
  logoDataUrl: z.string().max(400_000).optional(), // proje/firma logosu (PNG/JPEG data URL, ≤ ~300 KB); yoksa sağlayıcı logosu
  showSignatures: z.boolean().default(true),
  note: z.string().max(1000).optional(),          // kapak alt notu
});

export const ProjectInfoSchema = z.object({
  name: z.string().min(1),
  city: z.string().optional(), district: z.string().optional(),
  block: z.string().optional(), parcel: z.string().optional(),
  owner: z.string().optional(),
  /** proje konumu (WGS84, derece) — SS/S1 AFAD TDTH ızgarasından bununla okunur */
  location: z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180) }).optional(),
  engineer: z.object({ name: z.string(), chamberNo: z.string().optional(), title: z.string().optional() }).optional(),
  date: z.string(),
  revision: z.string(),
  notes: z.string().optional(),
  cover: ReportCoverSchema.optional(),
});

export const ProjectSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  info: ProjectInfoSchema,
  walls: z.array(WallInputSchema).min(1),
  reportSettings: ReportSettingsSchema,
  pricing: PricingSchema.default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ParametricGeometry = z.infer<typeof ParametricGeometrySchema>;
export type PolygonGeometry = z.infer<typeof PolygonGeometrySchema>;
export type RcCantileverGeometry = z.infer<typeof RcCantileverGeometrySchema>;
export type RcSettings = z.infer<typeof RcSettingsSchema>;
export type QuantitiesSettings = z.infer<typeof QuantitiesSettingsSchema>;
export type DrawingSettings = z.infer<typeof DrawingSettingsSchema>;
export type QuantityKey = z.infer<typeof QuantityKeySchema>;
export type PriceItem = z.infer<typeof PriceItemSchema>;
export type Pricing = z.infer<typeof PricingSchema>;
export type BarSpec = z.infer<typeof BarSpecSchema>;
export type ConcreteClass = z.infer<typeof ConcreteClassSchema>;
export type SteelGrade = z.infer<typeof SteelGradeSchema>;
export type RcDesignCode = Exclude<z.infer<typeof RcDesignCodeSchema>, "auto">;
export type StructureType = "masonry-gravity" | "rc-cantilever" | "rc-counterfort";
export type SoilLayer = z.infer<typeof SoilLayerSchema>;
export type Backfill = z.infer<typeof BackfillSchema>;
export type FoundationSoil = z.infer<typeof FoundationSoilSchema>;
export type Water = z.infer<typeof WaterSchema>;
export type LineLoad = z.infer<typeof LineLoadSchema>;
export type Loads = z.infer<typeof LoadsSchema>;
export type SiteClass = z.infer<typeof SiteClassSchema>;
export type Seismic = z.infer<typeof SeismicSchema>;
export type Material = z.infer<typeof MaterialSchema>;
export type CheckSettings = z.infer<typeof CheckSettingsSchema>;
export type GlobalStabilityInput = z.infer<typeof GlobalStabilitySchema>;
export type EcOptions = z.infer<typeof EcOptionsSchema>;
export type PressureMethod = CheckSettings["pressureMethod"];
export type PropertyLine = z.infer<typeof PropertyLineSchema>;
export type WallInput = z.infer<typeof WallInputSchema>;
export type ReportSettings = z.infer<typeof ReportSettingsSchema>;
export type ProjectInfo = z.infer<typeof ProjectInfoSchema>;
export type ReportCover = z.infer<typeof ReportCoverSchema>;
export type Project = z.infer<typeof ProjectSchema>;
